import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, Sparkles } from "lucide-react";
import { api, isNewProduct, NEW_PRODUCT_DAYS } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import PromoBanner from "@/components/PromoBanner";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "Todos";
  const q = params.get("q") || "";
  const onlyNew = params.get("novedades") === "1";
  const [search, setSearch] = useState(q);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(["Todos", ...r.data]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category !== "Todos") p.category = category;
    if (q) p.q = q;
    api.get("/products", { params: p }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [category, q]);

  const visible = useMemo(() => (onlyNew ? products.filter(isNewProduct) : products), [products, onlyNew]);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "Todos") next.delete("category"); else next.set("category", c);
    setParams(next);
  };

  const toggleNew = () => {
    const next = new URLSearchParams(params);
    if (onlyNew) next.delete("novedades"); else next.set("novedades", "1");
    setParams(next);
  };

  const onSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (search.trim()) next.set("q", search.trim()); else next.delete("q");
    setParams(next);
  };

  const title = useMemo(() => {
    if (q) return `Resultados para "${q}"`;
    if (onlyNew) return category !== "Todos" ? `Novedades en ${category}` : "Novedades";
    if (category && category !== "Todos") return category;
    return "Toda la tienda";
  }, [q, category, onlyNew]);

  return (
    <>
    <PromoBanner />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="shop-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">Catálogo</span>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight mt-1" data-testid="shop-title">{title}</h1>
          <p className="text-slate-500 text-sm mt-1">{visible.length} producto(s){onlyNew && ` · agregados en los últimos ${NEW_PRODUCT_DAYS} días`}</p>
        </div>
        <form onSubmit={onSearch} className="relative md:w-80">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-slate-200 focus:border-blue-400 outline-none"
            placeholder="Buscar producto..."
            data-testid="shop-search-input"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        </form>
      </div>

      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
        <SlidersHorizontal className="w-4 h-4" /> Filtrar por categoría:
      </div>
      <div className="flex flex-wrap gap-2 mb-10" data-testid="shop-category-filters">
        <button
          onClick={toggleNew}
          className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors inline-flex items-center gap-1.5 ${
            onlyNew ? "bg-green-600 text-white border-green-600" : "bg-green-50 text-green-700 border-green-200 hover:border-green-500"
          }`}
          data-testid="novedades-filter"
        >
          <Sparkles className="w-3.5 h-3.5" /> Novedades
        </button>
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              category === c || (category === "Todos" && c === "Todos")
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
            }`}
            data-testid={`category-filter-${c}`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20 text-slate-500">Cargando productos...</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200" data-testid="shop-empty">
          <p className="text-slate-600">{onlyNew ? `No hay productos nuevos en los últimos ${NEW_PRODUCT_DAYS} días.` : "No se encontraron productos."}</p>
          {onlyNew && <button onClick={toggleNew} className="mt-3 text-sm text-blue-600 hover:underline" data-testid="novedades-clear">Ver toda la tienda</button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {visible.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
    </>
  );
}
